import { sanitizeHtml } from "utils/sanitizeHtml";
import { useState } from "react";
import { useData } from "data/data-provider";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useInView } from "designs/DesignB/hooks/useInView";
import { sectionPaddingY } from "utils/sectionSpacing";

const FAQ = () => {
  const { faqs } = useData();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const sectionEntrance = useInView<HTMLDivElement>();
  const itemsEntrance = useInView<HTMLDivElement>();

  const items: any[] = faqs?.keypoints ?? [];
  const hasHeader = Boolean(faqs?.heading || faqs?.description);

  // 1NOW layout variant chosen in the FMS (Section.layout_variant):
  //   "two_column" → static 2-column grid, all Q/A visible at once (default)
  //   "accordion"  → click-to-expand rows, one open at a time
  // Default "two_column" so tenants who haven't picked a variant yet get
  // the 2-column grid.
  const layoutVariant: "accordion" | "two_column" =
    faqs?.layout_variant === "accordion" ? "accordion" : "two_column";

  if (!hasHeader && items.length === 0) return null;

  return (
    <section
      className={`relative ${sectionPaddingY(
        faqs?.vertical_spacing,
        "py-10 md:py-14 lg:py-20"
      )}`}
    >
      {/* Decorative gradient blob behind the header — matches WhyUs feel. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 -top-10 mx-auto h-72 w-[min(720px,90%)] rounded-full opacity-30 blur-3xl"
        style={{
          background:
            "radial-gradient(circle at center, var(--c-primary-200, #2dd4bf) 0%, transparent 65%)",
        }}
      />

      {/* Section header — spans the full container width to align with every
          other home-page section (Header logo, banner, About, Testimonials,
          WhyUs, Image Section, Footer). Earlier this was capped at
          `max-w-3xl` (768px) and centered, which made the FAQ block sit
          inside its own narrow column and break the page's vertical
          alignment chain on wider screens. */}
      {hasHeader && (
        <div
          ref={sectionEntrance.ref}
          className={`relative text-center mb-10 sm:mb-14 md:mb-16 transform-gpu transition-all duration-1000 ease-[cubic-bezier(0.16,1,0.3,1)]
            ${
              sectionEntrance.inView
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
            }`}
        >
          <span className="inline-block mb-4 px-4 py-1.5 text-[11px] sm:text-xs font-semibold tracking-[0.2em] uppercase rounded-full border border-primary-200/40 bg-primary-100/10 text-primary-300">
            FAQ
          </span>
          {faqs?.heading && (
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.1] mb-4 sm:mb-6 text-text-color
                [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(faqs.heading) }}
            />
          )}
          {faqs?.description && (
            <div
              className="text-base sm:text-lg text-secondary-text-color leading-relaxed
                [&_p]:mb-2 last:[&_p]:mb-0
                [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                [&_a]:text-primary-200 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(faqs.description) }}
            />
          )}
        </div>
      )}

      {/* Accordion list — full container width to match every other home
          section. Items still stack vertically; each row uses its own
          internal padding so long Q/A text wraps naturally.
          Only renders when layoutVariant === "accordion"; the two-column
          variant is handled in the block below. */}
      {layoutVariant === "accordion" && items.length > 0 && (
        <div
          ref={itemsEntrance.ref}
          className="relative space-y-3 sm:space-y-4"
        >
          {items.map((item: any, index: number) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                style={{
                  transitionDelay: itemsEntrance.inView
                    ? `${index * 70}ms`
                    : "0ms",
                }}
                className={`group rounded-2xl border bg-secondary-50/50 transform-gpu transition-all duration-700 ease-out
                  ${
                    isOpen
                      ? "border-primary-200 shadow-lg shadow-primary-200/15"
                      : "border-secondary-100 hover:border-primary-200/50"
                  }
                  ${
                    itemsEntrance.inView
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-6"
                  }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  aria-expanded={isOpen}
                  className="flex w-full items-center justify-between gap-4 px-5 sm:px-6 py-4 sm:py-5 text-left"
                >
                  <span
                    className="flex-1 text-base sm:text-lg font-semibold text-text-color
                      [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading ?? "") }}
                  />
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300
                      ${
                        isOpen
                          ? "bg-primary-200 text-on-primary rotate-180"
                          : "bg-primary-100/15 text-primary-300 group-hover:bg-primary-100/30"
                      }`}
                  >
                    <ChevronDownIcon className="h-4 w-4" />
                  </span>
                </button>

                {/* Smooth height transition using the grid-rows trick — animates
                    cleanly between collapsed and auto height without measuring. */}
                <div
                  className={`grid transition-all duration-300 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100"
                      : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div
                      className="px-5 sm:px-6 pb-5 sm:pb-6 text-sm sm:text-base text-secondary-text-color leading-relaxed
                        [&_p]:mb-2 last:[&_p]:mb-0
                        [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                        [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                        [&_a]:text-primary-200 [&_a]:underline"
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(item?.description ?? "",) }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Two-column variant — static grid, every Q/A pair visible at once.
          Each cell shows the question as a heading and the answer as body
          copy underneath. Better for short, scannable FAQ lists where the
          accordion friction isn't earning its keep. */}
      {layoutVariant === "two_column" && items.length > 0 && (
        <div
          ref={itemsEntrance.ref}
          className={`relative grid gap-5 sm:gap-6 md:gap-8 grid-cols-1 md:grid-cols-2 transform-gpu transition-all duration-700 ease-out ${
            itemsEntrance.inView
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-6"
          }`}
        >
          {items.map((item: any, index: number) => (
            <div
              key={index}
              className="rounded-2xl border border-secondary-100 bg-secondary-50/50 px-5 sm:px-6 py-5 sm:py-6 transition-colors duration-300 hover:border-primary-200/50"
            >
              <h3
                className="text-base sm:text-lg font-semibold text-text-color mb-2 sm:mb-3
                  [&_p]:m-0 [&_p]:inline [&_strong]:font-extrabold [&_em]:italic [&_u]:underline"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.heading ?? "") }}
              />
              <div
                className="text-sm sm:text-base text-secondary-text-color leading-relaxed
                  [&_p]:mb-2 last:[&_p]:mb-0
                  [&_ul]:list-disc [&_ul]:ps-5 [&_ol]:list-decimal [&_ol]:ps-5 [&_li]:mb-1
                  [&_strong]:font-semibold [&_em]:italic [&_u]:underline
                  [&_a]:text-primary-200 [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item?.description ?? "") }}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default FAQ;
